'use strict';

var React = require('react');
var reactUtils = require('@sravni/react-utils');
var reactTable = require('@tanstack/react-table');
var Space = require('../../Space/src/Space.js');
var useTable = require('../hooks/useTable.js');
var getDefaultColumns = require('../utils/getDefaultColumns.js');
var globalFilterFn = require('../utils/globalFilterFn.js');
var ExpandColumn = require('./ExpandColumn.js');
var GlobalSearch = require('./GlobalSearch.js');
var Head = require('./Head.js');
var NotFound = require('./NotFound.js');
var Pagination = require('./Pagination.js');
var Row = require('./Row.js');
var SubRow = require('./SubRow.js');
var TableView = require('./TableView.js');
var Thead = require('./Thead.js');

function _interopNamespaceDefault(e) {
    var n = Object.create(null);
    if (e) {
        Object.keys(e).forEach(function (k) {
            if (k !== 'default') {
                var d = Object.getOwnPropertyDescriptor(e, k);
                Object.defineProperty(n, k, d.get ? d : {
                    enumerable: true,
                    get: function () { return e[k]; }
                });
            }
        });
    }
    n.default = e;
    return Object.freeze(n);
}

var React__namespace = /*#__PURE__*/_interopNamespaceDefault(React);

/**
 * @description Table component.
 *
 * @component
 * @example
 * ```jsx
 * <Table>content</Table>
 * ```
 */
const InnerTable = (props, ref) => {
    const { options, className, topRightAddon, renderSubComponent, ...restProps } = props;
    const { manualPagination, data, columns, enableGlobalFilter = false, state, getRowCanExpand, onPaginationChange, ...restOptions } = options;
    const { pagination, ...restState } = state ?? {};
    const [isSearchFocused, setIsSearchFocused] = reactUtils.useBoolean(false);
    const [globalFilter, setGlobalFilter] = React__namespace.useState('');
    const [innerPagination, setInnerPagination] = React__namespace.useState({
        pageIndex: state?.pagination?.pageIndex ?? 0,
        pageSize: state?.pagination?.pageSize ?? 10,
    });
    const handleGlobalSearch = React__namespace.useCallback((event) => setGlobalFilter(String(event.currentTarget.value)), []);
    const handlePaginationChange = React__namespace.useCallback((updater) => {
        if (typeof updater !== 'function') {
            return;
        }
        const nextState = updater(innerPagination);
        setInnerPagination(updater);
        onPaginationChange?.(nextState?.pageIndex);
    }, [innerPagination, onPaginationChange]);
    const defaultColumns = getDefaultColumns.getDefaultColumns(data);
    const innerColumns = columns ?? defaultColumns;
    if (getRowCanExpand && ExpandColumn.ExpandColumn) {
        innerColumns.unshift(ExpandColumn.ExpandColumn);
    }
    const table = useTable.useTable({
        data,
        columns: innerColumns,
        enableGlobalFilter,
        manualPagination,
        getRowCanExpand,
        globalFilterFn: globalFilterFn.globalFilterFn,
        getCoreRowModel: reactTable.getCoreRowModel(),
        onGlobalFilterChange: setGlobalFilter,
        onPaginationChange: handlePaginationChange,
        state: {
            globalFilter,
            pagination: innerPagination,
            ...restState,
        },
        ...restOptions,
    });
    return (React__namespace.createElement("div", { ref: ref, className: className, "data-qa": "Table", ...restProps },
        (enableGlobalFilter || topRightAddon) && (React__namespace.createElement(Space.Space, { justify: "space-between", size: 8, className: "h-mb-24" },
            enableGlobalFilter && (React__namespace.createElement(GlobalSearch.GlobalSearch, { globalFilter: globalFilter, isSearchFocused: isSearchFocused, setIsSearchFocused: setIsSearchFocused, handleGlobalSearch: handleGlobalSearch })),
            topRightAddon)),
        globalFilter && table.getRowCount() === 0 ? (React__namespace.createElement(NotFound.NotFound, null)) : (React__namespace.createElement(Space.Space, { size: 24, direction: "vertical" },
            React__namespace.createElement(TableView.TableView, null,
                React__namespace.createElement(Thead.Thead, null, table.getHeaderGroups().map((headerGroup) => (React__namespace.createElement("tr", { key: `${headerGroup.id}-headergroup` }, headerGroup.headers.map((header) => (React__namespace.createElement(Head.Head, { key: `${header.id}-header`, header: header }))))))),
                React__namespace.createElement("tbody", null, table.getRowModel().rows.map((row) => (React__namespace.createElement(React__namespace.Fragment, { key: `${row.id}-row` },
                    React__namespace.createElement(Row.Row, { row: row }),
                    row.getIsExpanded() && !!renderSubComponent && (React__namespace.createElement(SubRow.SubRow, { key: `${row.id}-subrow` },
                        React__namespace.createElement("td", { colSpan: row.getVisibleCells().length, className: "h-p-16" }, renderSubComponent(row))))))))),
            React__namespace.createElement(Pagination.Pagination, { table: table, manualPagination: manualPagination })))));
};
InnerTable.displayName = 'Table';
const Table = React__namespace.forwardRef(InnerTable);

exports.Table = Table;
//# sourceMappingURL=Table.js.map
